import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Analytics as AnalyticsIcon,
  PowerSettingsNew as PowerIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Storage as StorageIcon,
  ShoppingCart as ShoppingCartIcon,
  Build as BuildIcon,
  Publish as PublishIcon,
  Telegram as TelegramIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  GitHub as GitHubIcon,
  Http as HttpIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { websitesAPI, telegramAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import TestForm from '../forms/TestForm';
import TestOrder from '../forms/TestOrder';

const WebsitesPage = () => {
  const { user } = useAuth();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [testFormOpen, setTestFormOpen] = useState(false);
  const [testFormWebsite, setTestFormWebsite] = useState(null);
  const [testOrderOpen, setTestOrderOpen] = useState(false);
  const [testOrderWebsite, setTestOrderWebsite] = useState(null);
  const [publishingWebsite, setPublishingWebsite] = useState(null);
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false);
  const [telegramConnectionLink, setTelegramConnectionLink] = useState('');
  const [loadingTelegram, setLoadingTelegram] = useState(false);
  const [publishConfirmDialogOpen, setPublishConfirmDialogOpen] = useState(false);
  const [websiteToPublish, setWebsiteToPublish] = useState(null);
  const [publishLimitData, setPublishLimitData] = useState(null);
  const [loadingPublishLimits, setLoadingPublishLimits] = useState(false);

  // Check if user is admin or superadmin
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const { control, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      name: '',
      domain: '',
      description: '',
      buildHookUrl: '',
      webhook: {
        type: 'simple',
        simpleWebhook: {
          url: ''
        },
        githubWebhook: {
          repository: '',
          token: '',
          eventType: 'redeploy'
        }
      },
      emailForm: {
        recipientEmail: '',
        subject: 'New Form Submission',
        autoReply: {
          enabled: false,
          message: 'Thank you for your message. We will get back to you soon.'
        }
      },
      settings: {
        attachments: {
          saveToStorage: true
        }
      }
    }
  });

  // Watch webhook type to conditionally show fields
  const watchedWebhookType = watch('webhook.type');

  useEffect(() => {
    fetchWebsites();
  }, []);

  const fetchWebsites = async () => {
    try {
      setLoading(true);
      const response = await websitesAPI.getWebsites();
      setWebsites(response.data.websites);
    } catch (error) {
      console.error('Error fetching websites:', error);
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (website = null) => {
    setEditingWebsite(website);
    if (website) {
      // Determine webhook configuration for existing website
      let webhookConfig = {
        type: 'simple',
        simpleWebhook: { url: '' },
        githubWebhook: { repository: '', token: '', eventType: 'redeploy' }
      };

      // Check for new webhook structure first
      if (website.webhook) {
        webhookConfig = {
          type: website.webhook.type || 'simple',
          simpleWebhook: {
            url: website.webhook.simpleWebhook?.url || ''
          },
          githubWebhook: {
            repository: website.webhook.githubWebhook?.repository || '',
            token: website.webhook.githubWebhook?.token || '',
            eventType: website.webhook.githubWebhook?.eventType || 'redeploy'
          }
        };
      }
      // Fallback to buildHookUrl for backward compatibility
      else if (website.buildHookUrl) {
        webhookConfig.simpleWebhook.url = website.buildHookUrl;
      }

      reset({
        name: website.name,
        domain: website.domain || '',
        description: website.description || '',
        buildHookUrl: website.buildHookUrl || '',
        webhook: webhookConfig,
        emailForm: {
          recipientEmail: website.emailForm.recipientEmail,
          subject: website.emailForm.subject,
          autoReply: {
            enabled: website.emailForm.autoReply.enabled,
            message: website.emailForm.autoReply.message
          }
        },
        settings: {
          attachments: {
            saveToStorage: website.settings?.attachments?.saveToStorage !== false
          },
          storage: {
            totalQuota: website.settings?.storage?.totalQuota || 104857600
          }
        }
      });
    } else {
      reset({
        name: '',
        domain: '',
        description: '',
        buildHookUrl: '',
        webhook: {
          type: 'simple',
          simpleWebhook: { url: '' },
          githubWebhook: { repository: '', token: '', eventType: 'redeploy' }
        },
        emailForm: {
          recipientEmail: '',
          subject: 'New Form Submission',
          autoReply: {
            enabled: false,
            message: 'Thank you for your message. We will get back to you soon.'
          }
        },
        settings: {
          attachments: {
            saveToStorage: true
          },
          storage: {
            totalQuota: 104857600 // 100MB default
          }
        }
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingWebsite(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      // Clean up empty domain field
      if (data.domain === '') {
        delete data.domain;
      }

      // Clean up empty webhook fields - don't send empty strings
      if (data.webhook) {
        // For simple webhooks, remove empty URL
        if (data.webhook.simpleWebhook?.url === '') {
          delete data.webhook.simpleWebhook.url;
        }

        // For GitHub webhooks, remove empty fields but keep the structure
        if (data.webhook.githubWebhook) {
          if (data.webhook.githubWebhook.repository === '') {
            delete data.webhook.githubWebhook.repository;
          }
          if (data.webhook.githubWebhook.token === '') {
            delete data.webhook.githubWebhook.token;
          }
          if (data.webhook.githubWebhook.eventType === '' || !data.webhook.githubWebhook.eventType) {
            data.webhook.githubWebhook.eventType = 'redeploy';
          }
        }
      }

      // Ensure complete settings structure for updates
      if (editingWebsite && data.settings) {
        // Merge with existing settings to avoid missing fields
        data.settings = {
          ...editingWebsite.settings,
          ...data.settings,
          attachments: {
            ...editingWebsite.settings?.attachments,
            ...data.settings.attachments
          },
          storage: {
            ...editingWebsite.settings?.storage,
            ...data.settings.storage
          }
        };
      }

      if (editingWebsite) {
        await websitesAPI.updateWebsite(editingWebsite._id, data);
        toast.success('Website updated successfully');
      } else {
        await websitesAPI.createWebsite(data);
        toast.success('Website created successfully');
      }
      handleCloseDialog();
      fetchWebsites();
    } catch (error) {
      console.error('Error saving website:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.message || 'Failed to save website');
    }
  };

  const handleDeleteClick = (website) => {
    setWebsiteToDelete(website);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await websitesAPI.deleteWebsite(websiteToDelete._id);
      toast.success('Website deleted successfully');
      setDeleteDialogOpen(false);
      setWebsiteToDelete(null);
      fetchWebsites();
    } catch (error) {
      console.error('Error deleting website:', error);
      toast.error('Failed to delete website');
    }
  };

  const handleToggleStatus = async (website) => {
    try {
      await websitesAPI.toggleStatus(website._id);
      toast.success(`Website ${website.status === 'active' ? 'deactivated' : 'activated'}`);
      fetchWebsites();
      handleMenuClose();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update website status');
    }
  };

  const handleMenuClick = (event, website) => {
    setMenuAnchor(event.currentTarget);
    setSelectedWebsite(website);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedWebsite(null);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleTestForm = (website) => {
    setTestFormWebsite(website);
    setTestFormOpen(true);
    handleMenuClose();
  };

  const handleCloseTestForm = () => {
    setTestFormOpen(false);
    setTestFormWebsite(null);
  };

  const handleTestOrder = (website) => {
    setTestOrderWebsite(website);
    setTestOrderOpen(true);
    handleMenuClose();
  };

  const handleCloseTestOrder = () => {
    setTestOrderOpen(false);
    setTestOrderWebsite(null);
  };

  const fetchPublishLimitData = async (website) => {
    try {
      setLoadingPublishLimits(true);
      // Fetch fresh website data to get latest publish limits
      const response = await websitesAPI.getWebsite(website._id);
      const updatedWebsite = response.data.website;

      // Calculate reset information
      const now = new Date();
      const dailyResetDate = updatedWebsite.settings?.publishRateLimit?.lastResetDate
        ? new Date(updatedWebsite.settings.publishRateLimit.lastResetDate)
        : now;
      const monthlyResetDate = updatedWebsite.settings?.publishRateLimit?.lastMonthlyResetDate
        ? new Date(updatedWebsite.settings.publishRateLimit.lastMonthlyResetDate)
        : now;

      // Calculate days until reset
      const nextDailyReset = new Date(dailyResetDate);
      nextDailyReset.setDate(nextDailyReset.getDate() + 1);
      const daysUntilDailyReset = Math.ceil((nextDailyReset - now) / (1000 * 60 * 60 * 24));

      const nextMonthlyReset = new Date(monthlyResetDate);
      nextMonthlyReset.setMonth(nextMonthlyReset.getMonth() + 1);
      const daysUntilMonthlyReset = Math.ceil((nextMonthlyReset - now) / (1000 * 60 * 60 * 24));

      const limitData = {
        dailyPublishes: updatedWebsite.settings?.publishRateLimit?.dailyPublishes || 0,
        dailyLimit: updatedWebsite.settings?.publishRateLimit?.dailyLimit || 3,
        monthlyPublishes: updatedWebsite.settings?.publishRateLimit?.monthlyPublishes || 0,
        monthlyLimit: updatedWebsite.settings?.publishRateLimit?.monthlyLimit || 10,
        daysUntilDailyReset: Math.max(0, daysUntilDailyReset),
        daysUntilMonthlyReset: Math.max(0, daysUntilMonthlyReset),
        webhookType: updatedWebsite.webhook?.type || (updatedWebsite.buildHookUrl ? 'simple' : 'unknown')
      };

      setPublishLimitData(limitData);
      return limitData;
    } catch (error) {
      console.error('Error fetching publish limit data:', error);
      toast.error('Failed to fetch latest publish limits');
      return null;
    } finally {
      setLoadingPublishLimits(false);
    }
  };

  const handlePublishWebsite = async (website) => {
    // Check if any webhook is properly configured
    const hasSimpleWebhook = website.webhook?.simpleWebhook?.url || website.buildHookUrl;
    const hasGithubWebhook = website.webhook?.githubWebhook?.repository && website.webhook?.githubWebhook?.token;

    if (!hasSimpleWebhook && !hasGithubWebhook) {
      toast.error('No webhook configured for this website');
      return;
    }

    // Show confirmation dialog with latest publish limits
    setWebsiteToPublish(website);
    await fetchPublishLimitData(website);
    setPublishConfirmDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmPublish = async () => {
    if (!websiteToPublish) return;

    try {
      setPublishingWebsite(websiteToPublish._id);
      setPublishConfirmDialogOpen(false);

      const response = await websitesAPI.publishWebsite(websiteToPublish._id);

      if (response.data.success) {
        const publishInfo = response.data.publishInfo;
        const remainingMonthly = publishInfo.remainingMonthlyPublishes;
        const remainingDaily = publishInfo.remainingDailyPublishes;

        // Show success message with deployment timing
        toast.success(
          `🚀 Website published successfully! ${response.data.webhookType === 'github' ? 'GitHub dispatch' : 'Build hook'} triggered.\n` +
          `✨ Your changes will be live in 3-4 minutes.\n` +
          `📊 Remaining: ${remainingDaily} daily, ${remainingMonthly} monthly`,
          { duration: 6000 }
        );
      } else {
        toast.error(response.data.message || 'Failed to publish website');
      }
    } catch (error) {
      console.error('Error publishing website:', error);

      if (error.response?.status === 429) {
        const errorData = error.response.data;
        if (errorData.message?.includes('Monthly')) {
          toast.error(`Monthly publish limit reached (${errorData.monthlyLimit || 10}/month). Limit resets next month.`);
        } else {
          toast.error(`Daily publish limit reached (${errorData.dailyLimit || 3}/day). Try again tomorrow.`);
        }
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.message || 'Invalid request');
      } else if (error.response?.status === 502) {
        toast.error(`Webhook failed: ${error.response.data.message || 'Unknown error'}`);
      } else {
        toast.error('Failed to publish website');
      }
    } finally {
      setPublishingWebsite(null);
      setWebsiteToPublish(null);
      setPublishLimitData(null);
    }
  };

  const handleCancelPublish = () => {
    setPublishConfirmDialogOpen(false);
    setWebsiteToPublish(null);
    setPublishLimitData(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const handleTelegramConnection = async () => {
    try {
      setLoadingTelegram(true);
      const response = await telegramAPI.getConnectionLink(user._id);
      setTelegramConnectionLink(response.data.data.connectionLink);
      setTelegramDialogOpen(true);
    } catch (error) {
      console.error('Error getting Telegram connection link:', error);
      toast.error('Failed to generate Telegram connection link');
    } finally {
      setLoadingTelegram(false);
    }
  };

  const handleCloseTelegramDialog = () => {
    setTelegramDialogOpen(false);
    setTelegramConnectionLink('');
  };

  const handleCopyTelegramLink = () => {
    navigator.clipboard.writeText(telegramConnectionLink);
    toast.success('Telegram connection link copied to clipboard!');
  };

  const handleOpenTelegramLink = () => {
    window.open(telegramConnectionLink, '_blank');
  };

  const isUserTelegramConnected = () => {
    return user?.telegram?.chatId ? true : false;
  };

  const getTelegramConnectionDate = () => {
    if (user?.telegram?.connectedAt) {
      return new Date(user.telegram.connectedAt).toLocaleDateString();
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            My Websites
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your websites and email forms
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {/* Telegram Connection Button */}
          <Button
            variant={isUserTelegramConnected() ? "outlined" : "contained"}
            startIcon={
              loadingTelegram ? (
                <CircularProgress size={16} color="inherit" />
              ) : isUserTelegramConnected() ? (
                <CheckCircleIcon />
              ) : (
                <TelegramIcon />
              )
            }
            onClick={handleTelegramConnection}
            disabled={loadingTelegram}
            color={isUserTelegramConnected() ? "success" : "primary"}
            size="large"
          >
            {loadingTelegram
              ? 'Loading...'
              : isUserTelegramConnected()
                ? 'Telegram Connected'
                : 'Connect Telegram'
            }
          </Button>

          {/* Add Website button - Only show for admin */}
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="large"
            >
              Add Website
            </Button>
          )}
        </Box>
      </Box>

      {/* Telegram Connection Status */}
      {isUserTelegramConnected() && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="body2">
                <strong>Telegram Connected!</strong> You'll receive notifications about new orders and form submissions.
              </Typography>
              {getTelegramConnectionDate() && (
                <Typography variant="caption" color="text.secondary">
                  Connected on {getTelegramConnectionDate()}
                </Typography>
              )}
            </Box>
            <Button
              size="small"
              onClick={handleTelegramConnection}
              startIcon={<LinkIcon />}
            >
              Get New Link
            </Button>
          </Box>
        </Alert>
      )}

      {/* Websites Grid */}
      {websites.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <LanguageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No websites yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Create your first website to start collecting form submissions
            </Typography>
            {/* Add Your First Website button - Only show for admin */}
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add Your First Website
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {websites.map((website) => (
            <Grid item xs={12} sm={6} md={4} key={website._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {website.name}
                      </Typography>
                      <Chip
                        label={website.status}
                        color={getStatusColor(website.status)}
                        size="small"
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, website)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  {/* Website Info */}
                  <Box mb={2}>
                    {website.domain && (
                      <Box display="flex" alignItems="center" mb={1}>
                        <LanguageIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {website.domain}
                        </Typography>
                        <Tooltip title="Copy domain">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(website.domain, 'Domain')}
                          >
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        Site ID:
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {website.siteId}
                      </Typography>
                      <Tooltip title="Copy Site ID (Globally Unique)">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(website.siteId, 'Site ID')}
                        >
                          <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {!website.domain && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Domain not set - can be added later
                      </Typography>
                    )}
                  </Box>

                  {/* Description */}
                  {website.description && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {website.description}
                    </Typography>
                  )}

                  {/* Email Form Info */}
                  <Box display="flex" alignItems="center" mb={1}>
                    <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {website.emailForm.recipientEmail}
                    </Typography>
                  </Box>

                  {/* Attachment Storage Status - Only show for admin */}
                  {isAdmin && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <AttachFileIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        File Storage:
                      </Typography>
                      <Chip
                        label={website.settings?.attachments?.saveToStorage !== false ? 'Enabled' : 'Disabled'}
                        color={website.settings?.attachments?.saveToStorage !== false ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  )}

                  {/* Webhook Status - Only show for admin */}
                  {isAdmin && (
                    <Box display="flex" alignItems="center" mb={1}>
                      {website.webhook?.type === 'github' || (!website.webhook && !website.buildHookUrl) ? (
                        <GitHubIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      ) : (
                        <HttpIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Webhook:
                      </Typography>
                      <Chip
                        label={(() => {
                          // Check for GitHub webhook
                          if (website.webhook?.type === 'github') {
                            const hasRepo = website.webhook?.githubWebhook?.repository;
                            const hasToken = website.webhook?.githubWebhook?.token;
                            return hasRepo && hasToken ? 'GitHub Configured' : 'GitHub Not Set';
                          }

                          // Check for Simple webhook (new structure or legacy)
                          const hasSimpleUrl = website.webhook?.simpleWebhook?.url;
                          const hasLegacyUrl = website.buildHookUrl;

                          return hasSimpleUrl || hasLegacyUrl ? 'Simple Configured' : 'Not Set';
                        })()}
                        color={(() => {
                          // Check for GitHub webhook
                          if (website.webhook?.type === 'github') {
                            return website.webhook?.githubWebhook?.repository && website.webhook?.githubWebhook?.token ? 'success' : 'default';
                          }

                          // Check for Simple webhook
                          return website.webhook?.simpleWebhook?.url || website.buildHookUrl ? 'success' : 'default';
                        })()}
                        size="small"
                        sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                      />
                    </Box>
                  )}

                  {/* Form Analytics */}
                  <Box mt={2}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center">
                        <AnalyticsIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {website.analytics.totalSubmissions} form submissions
                        </Typography>
                      </Box>
                      {website.analytics.lastSubmission && (
                        <Typography variant="caption" color="text.secondary">
                          Last: {new Date(website.analytics.lastSubmission).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    {/* Form Limits - Only show for admin */}
                    {isAdmin && (
                      <Typography variant="caption" color="text.secondary">
                        Form Limits: {website.settings?.rateLimit?.dailySubmissions || 0}/{website.settings?.rateLimit?.dailyLimit || 5} daily, {website.analytics.monthlySubmissions}/{website.settings?.rateLimit?.monthlyLimit || 50} monthly
                      </Typography>
                    )}
                  </Box>

                  {/* Order Analytics */}
                  <Box mt={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center">
                        <ShoppingCartIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {website.analytics?.totalOrders || 0} orders
                        </Typography>
                      </Box>
                      {website.analytics?.lastOrder && (
                        <Typography variant="caption" color="text.secondary">
                          Last: {new Date(website.analytics.lastOrder).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    {/* Order Limits - Only show for admin */}
                    {isAdmin && (
                      <Typography variant="caption" color="text.secondary">
                        Order Limits: {website.settings?.orderRateLimit?.dailyOrders || 0}/{website.settings?.orderRateLimit?.dailyLimit || 10} daily, {website.analytics?.monthlyOrders || 0}/{website.settings?.orderRateLimit?.monthlyLimit || 100} monthly
                      </Typography>
                    )}
                  </Box>

                  {/* Storage Usage - Only show for admin */}
                  {isAdmin && website.storageInfo && (
                    <Box mt={2}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center">
                          <StorageIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Storage: {website.storageInfo.currentUsageFormatted} / {website.storageInfo.totalQuotaFormatted}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${website.storageInfo.usagePercentage}%`}
                          size="small"
                          color={website.storageInfo.isOverLimit ? 'error' : website.storageInfo.isNearLimit ? 'warning' : 'default'}
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                      </Box>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(website.storageInfo.usagePercentage, 100)}
                          color={website.storageInfo.isOverLimit ? 'error' : website.storageInfo.isNearLimit ? 'warning' : 'primary'}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  {/* Edit button - Only show for admin */}
                  {isAdmin && (
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenDialog(website)}
                    >
                      Edit
                    </Button>
                  )}
                  {/* <Button 
                    size="small" 
                    startIcon={<SendIcon />}
                    onClick={() => handleTestForm(website)}
                    color="primary"
                  >
                    Test Form
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => handleTestOrder(website)}
                    color="secondary"
                  >
                    Test Order
                  </Button> */}
                  {/* Publish button - Only show for admin and if any webhook is configured */}
                  {isAdmin && (() => {
                    // Check webhook configuration
                    const simpleUrl = website.webhook?.simpleWebhook?.url;
                    const githubRepo = website.webhook?.githubWebhook?.repository;
                    const githubToken = website.webhook?.githubWebhook?.token;
                    const legacyUrl = website.buildHookUrl;

                    const hasWebhook = simpleUrl || (githubRepo && githubToken) || legacyUrl;

                    return hasWebhook;
                  })() && (
                      <Button
                        size="small"
                        startIcon={publishingWebsite === website._id ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                        onClick={() => handlePublishWebsite(website)}
                        disabled={publishingWebsite === website._id}
                        sx={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#45a049',
                          },
                          '&:disabled': {
                            backgroundColor: '#81c784',
                            color: 'white',
                          }
                        }}
                      >
                        {publishingWebsite === website._id
                          ? 'Publishing...'
                          : `Publish (${(website.settings?.publishRateLimit?.monthlyPublishes || 0)}/${(website.settings?.publishRateLimit?.monthlyLimit || 10)})`
                        }
                      </Button>
                    )}
                  {website.domain && (
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => window.open(`https://${website.domain}`, '_blank')}
                    >
                      Visit
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {/* Edit Website - Only show for admin */}
        {isAdmin && (
          <MenuItem onClick={() => handleOpenDialog(selectedWebsite)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Website</ListItemText>
          </MenuItem>
        )}
        {isAdmin && <MenuItem onClick={() => handleTestForm(selectedWebsite)}>
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Test Form</ListItemText>
        </MenuItem>}
        {isAdmin && <MenuItem onClick={() => handleTestOrder(selectedWebsite)}>
          <ListItemIcon>
            <ShoppingCartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Test Order</ListItemText>
        </MenuItem>}
        {/* Publish Website - Only show for admin and if any webhook is configured */}
        {isAdmin && (
          selectedWebsite?.webhook?.simpleWebhook?.url ||
          (selectedWebsite?.webhook?.githubWebhook?.repository && selectedWebsite?.webhook?.githubWebhook?.token) ||
          selectedWebsite?.buildHookUrl
        ) && (
            <MenuItem
              onClick={() => handlePublishWebsite(selectedWebsite)}
              disabled={publishingWebsite === selectedWebsite?._id}
            >
              <ListItemIcon>
                {publishingWebsite === selectedWebsite?._id ? (
                  <CircularProgress size={16} />
                ) : (
                  <PublishIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>
                {publishingWebsite === selectedWebsite?._id ? 'Publishing...' : 'Publish Website'}
              </ListItemText>
            </MenuItem>
          )}
        {/* Toggle Status - Only show for admin */}
        {isAdmin && (
          <MenuItem onClick={() => handleToggleStatus(selectedWebsite)}>
            <ListItemIcon>
              <PowerIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {selectedWebsite?.status === 'active' ? 'Deactivate' : 'Activate'}
            </ListItemText>
          </MenuItem>
        )}
        {/* Only show divider and delete if admin has access */}
        {isAdmin && <Divider />}
        {/* Delete Website - Only show for admin */}
        {isAdmin && (
          <MenuItem onClick={() => handleDeleteClick(selectedWebsite)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Website</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingWebsite ? 'Edit Website' : 'Add New Website'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Basic Info */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Website name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Website Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message || 'A globally unique Site ID will be generated automatically'}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="domain"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!value || value.trim() === '') return true; // Allow empty
                      const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
                      return domainRegex.test(value) || 'Please enter a valid domain or subdomain (e.g., example.com, sub.example.com)';
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Domain (Optional)"
                      fullWidth
                      error={!!errors.domain}
                      helperText={errors.domain?.message || 'Your website domain - can be added later (e.g., example.com, sub.example.com)'}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      fullWidth
                      multiline
                      rows={2}
                      helperText="Optional description of your website"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="buildHookUrl"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!value || value.trim() === '') return true; // Allow empty
                      try {
                        new URL(value);
                        return true;
                      } catch {
                        return 'Please enter a valid URL';
                      }
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Build Hook URL (Legacy - Optional)"
                      fullWidth
                      error={!!errors.buildHookUrl}
                      helperText={errors.buildHookUrl?.message || 'Legacy webhook URL for backward compatibility'}
                      placeholder="https://api.netlify.com/build_hooks/..."
                    />
                  )}
                />
              </Grid>

              {/* Webhook Configuration */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PublishIcon color="primary" />
                  <Typography variant="h6">
                    Webhook Configuration
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose how to trigger website deployments. Monthly limit: 10 publishes per month.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="webhook.type"
                  control={control}
                  render={({ field }) => (
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Webhook Type</FormLabel>
                      <RadioGroup
                        {...field}
                        row
                        sx={{ mt: 1 }}
                      >
                        <FormControlLabel
                          value="simple"
                          control={<Radio />}
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <HttpIcon />
                              <Typography>Simple Webhook</Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="github"
                          control={<Radio />}
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <GitHubIcon />
                              <Typography>GitHub Repository Dispatch</Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Simple Webhook Fields */}
              {watchedWebhookType === 'simple' && (
                <Grid item xs={12}>
                  <Controller
                    name="webhook.simpleWebhook.url"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!value || value.trim() === '') return true; // Allow empty
                        try {
                          new URL(value);
                          return true;
                        } catch {
                          return 'Please enter a valid URL';
                        }
                      }
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Webhook URL"
                        fullWidth
                        error={!!errors.webhook?.simpleWebhook?.url}
                        helperText={errors.webhook?.simpleWebhook?.url?.message || 'URL to trigger when publishing (e.g., Netlify, Vercel build hook)'}
                        placeholder="https://api.netlify.com/build_hooks/..."
                      />
                    )}
                  />
                </Grid>
              )}

              {/* GitHub Webhook Fields */}
              {watchedWebhookType === 'github' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="webhook.githubWebhook.repository"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (!value || value.trim() === '') return true; // Allow empty
                          return /^[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+$/.test(value) || 'Format should be: owner/repo';
                        }
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="GitHub Repository"
                          fullWidth
                          error={!!errors.webhook?.githubWebhook?.repository}
                          helperText={errors.webhook?.githubWebhook?.repository?.message || 'Format: owner/repository-name'}
                          placeholder="username/repository-name"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="webhook.githubWebhook.eventType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Event Type"
                          fullWidth
                          helperText="Event type for repository dispatch"
                          placeholder="redeploy"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="webhook.githubWebhook.token"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="GitHub Personal Access Token"
                          type="text"
                          fullWidth
                          error={!!errors.webhook?.githubWebhook?.token}
                          helperText="GitHub PAT with 'repo' permissions for repository dispatches"
                          placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              {/* Email Form Settings */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Email Form Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Default limits: 5 submissions per day, 50 per month
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="emailForm.recipientEmail"
                  control={control}
                  rules={{
                    required: 'Recipient email is required',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Please enter a valid email address'
                    }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Recipient Email"
                      type="email"
                      fullWidth
                      error={!!errors.emailForm?.recipientEmail}
                      helperText={errors.emailForm?.recipientEmail?.message || 'Where form submissions will be sent'}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="emailForm.subject"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Email Subject"
                      fullWidth
                      helperText="Subject line for form submission emails"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="emailForm.autoReply.enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Enable Auto-Reply"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="emailForm.autoReply.message"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Auto-Reply Message"
                      fullWidth
                      multiline
                      rows={3}
                      helperText="Message sent automatically to form submitters"
                    />
                  )}
                />
              </Grid>

              {/* Attachment Settings */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AttachFileIcon color="primary" />
                  <Typography variant="h6">
                    Attachment Settings
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Control how file attachments are handled in form submissions
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="settings.attachments.saveToStorage"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Save Attachments to Storage"
                    />
                  )}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  When enabled, uploaded files are saved to Backblaze B2 storage and download links are included in emails.
                  When disabled, only file information (name, size, type) is included in emails without storing the actual files.
                  <br />
                  <strong>Note:</strong> Emails will always include attachment information regardless of this setting.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="settings.storage.totalQuota"
                  control={control}
                  rules={{
                    required: 'Storage quota is required',
                    min: { value: 10485760, message: 'Minimum storage quota is 10MB' },
                    max: { value: 1073741824, message: 'Maximum storage quota is 1GB' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Storage Quota (bytes)"
                      type="number"
                      fullWidth
                      error={!!errors.settings?.storage?.totalQuota}
                      helperText={
                        errors.settings?.storage?.totalQuota?.message ||
                        `Current: ${Math.round(field.value / 1048576)}MB (Default: 100MB = 104857600 bytes)`
                      }
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={20} /> : (editingWebsite ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Website</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All associated data will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{websiteToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Form Dialog */}
      <Dialog
        open={testFormOpen}
        onClose={handleCloseTestForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Test Form Submission
        </DialogTitle>
        <DialogContent>
          {testFormWebsite && (
            <TestForm
              website={testFormWebsite}
              onClose={handleCloseTestForm}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestForm}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Order Dialog */}
      <Dialog
        open={testOrderOpen}
        onClose={handleCloseTestOrder}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Test Order Submission
        </DialogTitle>
        <DialogContent>
          {testOrderWebsite && (
            <TestOrder
              website={testOrderWebsite}
              onClose={handleCloseTestOrder}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestOrder}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Telegram Connection Dialog */}
      <Dialog
        open={telegramDialogOpen}
        onClose={handleCloseTelegramDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TelegramIcon color="primary" />
            <Typography variant="h6">
              Connect Telegram for Notifications
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {isUserTelegramConnected() ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Your Telegram account is already connected. You can use this link to reconnect or share with another device.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Connect your Telegram account to receive instant notifications about new orders and form submissions.
                </Typography>
              </Alert>
            )}

            <Typography variant="h6" gutterBottom>
              How to Connect:
            </Typography>
            <Box component="ol" sx={{ pl: 2, mb: 3 }}>
              <li>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Click the "Open Telegram" button below or copy the link
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This will open Telegram and start a chat with the notification bot
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Click "Start" in the Telegram chat to complete the connection
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  You'll receive a confirmation message once connected
                </Typography>
              </li>
            </Box>

            <Box sx={{
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              mb: 2
            }}>
              <Typography variant="body2" sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                fontSize: '0.85rem'
              }}>
                {telegramConnectionLink}
              </Typography>
            </Box>

            <Box display="flex" gap={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<TelegramIcon />}
                onClick={handleOpenTelegramLink}
                size="large"
                sx={{
                  bgcolor: '#0088cc',
                  '&:hover': {
                    bgcolor: '#006699'
                  }
                }}
              >
                Open Telegram
              </Button>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={handleCopyTelegramLink}
                size="large"
              >
                Copy Link
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              <strong>Note:</strong> The link is unique to your account and expires after use for security.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTelegramDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog
        open={publishConfirmDialogOpen}
        onClose={handleCancelPublish}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PublishIcon color="primary" />
            <Typography variant="h6">
              Confirm Website Publish
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingPublishLimits ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Getting latest publish limits...
              </Typography>
            </Box>
          ) : publishLimitData ? (
            <Box>
              <Typography variant="body1" gutterBottom>
                You are about to publish <strong>{websiteToPublish?.name}</strong>
              </Typography>

              <Alert severity="info" sx={{ my: 2 }}>
                <Typography variant="body2">
                  <strong>🚀 Deployment Timeline:</strong> Your changes will be live in 3-4 minutes after publishing.
                </Typography>
              </Alert>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  📊 Current Publish Limits
                </Typography>

                {/* Webhook Type */}
                {/* <Box display="flex" alignItems="center" mb={2}>
                  {publishLimitData.webhookType === 'github' ? (
                    <GitHubIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  ) : (
                    <HttpIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  )}
                  <Typography variant="body2">
                    <strong>Webhook Type:</strong> {publishLimitData.webhookType === 'github' ? 'GitHub Repository Dispatch' : 'Simple Webhook'}
                  </Typography>
                </Box> */}

                {/* Daily Limits */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">
                    <strong>Daily Usage:</strong>
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {publishLimitData.dailyPublishes} / {publishLimitData.dailyLimit}
                    </Typography>
                    <Chip
                      label={`${publishLimitData.dailyLimit - publishLimitData.dailyPublishes} remaining`}
                      size="small"
                      color={publishLimitData.dailyPublishes >= publishLimitData.dailyLimit ? 'error' : 'success'}
                    />
                  </Box>
                </Box>

                {/* Monthly Limits */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="body2">
                    <strong>Monthly Usage:</strong>
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {publishLimitData.monthlyPublishes} / {publishLimitData.monthlyLimit}
                    </Typography>
                    <Chip
                      label={`${publishLimitData.monthlyLimit - publishLimitData.monthlyPublishes} remaining`}
                      size="small"
                      color={publishLimitData.monthlyPublishes >= publishLimitData.monthlyLimit ? 'error' : 'success'}
                    />
                  </Box>
                </Box>

                {/* Reset Information */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>🔄 Limit Resets:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Daily limits reset in {publishLimitData.daysUntilDailyReset} day(s)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Monthly limits reset in {publishLimitData.daysUntilMonthlyReset} day(s)
                </Typography>
              </Box>

              {/* Warning if at limits */}
              {(publishLimitData.dailyPublishes >= publishLimitData.dailyLimit ||
                publishLimitData.monthlyPublishes >= publishLimitData.monthlyLimit) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      ⚠️ You have reached your {publishLimitData.dailyPublishes >= publishLimitData.dailyLimit ? 'daily' : 'monthly'} publish limit.
                      This publish attempt will be blocked.
                    </Typography>
                  </Alert>
                )}
            </Box>
          ) : (
            <Typography variant="body2" color="error">
              Failed to load publish limit data. Please try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPublish}>
            No, Cancel
          </Button>
          <Button
            onClick={handleConfirmPublish}
            variant="contained"
            disabled={
              loadingPublishLimits ||
              !publishLimitData ||
              publishingWebsite === websiteToPublish?._id ||
              (publishLimitData && (
                publishLimitData.dailyPublishes >= publishLimitData.dailyLimit ||
                publishLimitData.monthlyPublishes >= publishLimitData.monthlyLimit
              ))
            }
            startIcon={publishingWebsite === websiteToPublish?._id ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
            sx={{
              bgcolor: '#4caf50',
              '&:hover': {
                bgcolor: '#45a049',
              }
            }}
          >
            {publishingWebsite === websiteToPublish?._id ? 'Publishing...' : 'Yes, Publish'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebsitesPage; 